/**
 * React binding for the single WS state stream (M2.2).
 *
 * `useGameConnection(ticket)` opens one {@link WsClient} to the game for a
 * validated ticket, subscribes the component tree to the {@link GameStore}
 * snapshot via `useSyncExternalStore` (idempotent render by `version`), and hands
 * back a `send` function for issuing commands. The connection is torn down on
 * unmount / ticket change.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { Command } from '@jeopardy/shared';
import { GameStore, WsClient, type GameStoreSnapshot } from '@jeopardy/client-core';
import { wsUrlForTicket } from '../config';
import { recordWsReconnect } from './rum';
import { setVoiceTicket } from '../voice/voice-config';

/**
 * The connection lifecycle, distinct from a single `connected` boolean so the UI
 * can tell "no socket yet" (a host pre-create) apart from "actively reconnecting"
 * (was connected, dropped) — the former must NOT read "Reconnecting…".
 */
export type ConnectionStatus = 'idle' | 'connecting' | 'reconnecting' | 'connected';

/**
 * Browser implementation of the client-core `registerVisibility` port (#247): fire `cb`
 * when the page becomes visible again (the transition that throttled/froze the sync
 * timers). Returns an unregister fn; a no-op where `document` is unavailable (SSR). The
 * transport core is platform-agnostic, so the DOM-specific listener is injected here.
 */
function registerBrowserVisibility(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const handler = (): void => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}

export interface GameConnection {
  snapshot: GameStoreSnapshot;
  /** True iff the socket is currently open. */
  connected: boolean;
  /** The full lifecycle state for the connection badge. */
  status: ConnectionStatus;
  send: (command: Command) => void;
  /**
   * #264 (FR-4.13): the clueId whose buzzer this client has unlocked LOCALLY on the scheduled
   * fair-open instant, or `null`. The player screen ORs `=== clueState.clueId` into `canBuzz`
   * so a synced buzzer unlocks at the shared `OPEN_AT` — ahead of the authoritative
   * `buzzer_opened` frame — without disturbing the early-smash / reopened-buzz gates.
   */
  scheduledOpenClueId: string | null;
}

export function useGameConnection(ticket: string | null): GameConnection {
  const store = useMemo(() => new GameStore(), []);
  const clientRef = useRef<WsClient | null>(null);
  // `idle` until we have a ticket to connect with — no socket is opened before that,
  // so the badge must not claim we're reconnecting.
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  // #264 (FR-4.13): the clue whose buzzer this client unlocked locally on the scheduled open.
  const [scheduledOpenClueId, setScheduledOpenClueId] = useState<string | null>(null);

  useEffect(() => {
    if (!ticket) {
      setStatus('idle');
      return;
    }
    // A ticket exists → we're attempting the FIRST open (connecting). Subsequent
    // drops flip to `reconnecting` (below), which the WsClient's backoff drives.
    setStatus('connecting');
    // Publish the ticket so the backend voice adapters (#93) can authenticate their
    // /api/voice/tts + /api/voice/stt calls; cleared on teardown below.
    setVoiceTicket(ticket);
    const client = new WsClient({
      url: wsUrlForTicket(ticket),
      store,
      // WsClient reports true on open, false on close. Map close→reconnecting only
      // (the client auto-reconnects); open→connected.
      onOpenChange: (open) => setStatus(open ? 'connected' : 'reconnecting'),
      // Report successful reconnects to RUM (EI-4 / #35). The client-core transport is
      // platform-agnostic, so the web client injects the RUM helper here.
      onReconnect: recordWsReconnect,
      // #247: inject the browser visibility listener (DOM-specific; client-core defaults
      // to a no-op).
      registerVisibility: registerBrowserVisibility,
      // #264 (FR-4.13): surface the local scheduled-open into React state for `canBuzz`.
      onScheduledOpenChange: setScheduledOpenClueId,
    });
    clientRef.current = client;
    client.connect();
    return () => {
      client.close();
      clientRef.current = null;
      setVoiceTicket(null);
      setStatus('idle');
      setScheduledOpenClueId(null);
      store.reset();
    };
  }, [ticket, store]);

  const connected = status === 'connected';

  const snapshot = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSnapshot(),
    () => store.getSnapshot(),
  );

  const send = useMemo(() => (command: Command) => clientRef.current?.send(command), []);

  return { snapshot, connected, status, send, scheduledOpenClueId };
}
